/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: index.ts
 * Author: Austin Robinson
 * Date Created: 9/10/2022
 * Copyright (c) DrivingDoc 2022
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Interval } from '@flatten-js/interval-tree';
import { DateSelectArg } from '@fullcalendar/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import moment from 'moment';

import { listCoursePeriodsQueryKey, listCourseSectionsOptions, listCoursesOptions, getCourseInstructorPoolOptions } from '@drivingdoc/api-client/generated/react-query';
import { ListCoursePeriodsData, Options } from '@drivingdoc/api-client/generated';
import { Instructor } from 'types/instructors';
import { GetSectionsItem } from 'types/sections';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog/FeedbackDialog.tsx';

import { getApiErrorMessage } from 'utils/getApiErrorMessage.ts';

import useCalendarData from '../useCalendarData';
import Scheduler, { CreateCoursePeriodsOptions } from './Scheduler.ts';
import SchedulingPanel from './SchedulingPanel.tsx';

type PropTypes = {
  calendarRef: any;
};

export default function useScheduler({ calendarRef }: PropTypes) {
  const { data: courses } = useQuery({
    ...listCoursesOptions(),
    initialData: [],
  });

  const [open, setOpen] = useState(false);
  const [disableSelection, setDisableSelection] = useState(true);
  const [currentInterval, setCurrentInterval] = useState<Interval>(new Interval(0, 0));
  const [visibleResources, setVisibleResources] = useState<Set<string>>(new Set());
  const [shouldRerender, setShouldRerender] = useState(false);
  const [schedulingOptions, setSchedulingOptions] = useState<CreateCoursePeriodsOptions>({
    // location: tempLocations[0],
    // driveType: tempDriveTypes[0],
    course_id: undefined,
    section_id: undefined,
    duration: 60,
    capacity: 1,
    observationCapacity: 0,
    waitlistCapacity: 0,
    openRegistration: moment(),
    closeRegistration: 60,
  });
  const [selectedCourseType, setSelectedCourseType] = useState<
    'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online' | undefined
  >(undefined);
  const dialogs = useDialogs();
  const calendarData = useCalendarData();
  const queryClient = useQueryClient();
  const initialScrollPosition = useRef<number | null>(null);
  const scheduler = useRef(new Scheduler(calendarRef));

  // const { data: instructors, isFetched: instructorsFetched } = useQuery(['course_instructor_pool', schedulingOptions.driveType.courseId], async () => await getCourseInstructorPool({ courseId: schedulingOptions.driveType.courseId }), {
  //   refetchOnWindowFocus: false,
  //   refetchOnMount: false,
  //   refetchOnReconnect: false,
  // }) as any;

  const { data: sections } = useQuery({
    ...listCourseSectionsOptions({
      query: {
        courseId: schedulingOptions.course_id,
        page: 1,
        pageSize: 100,
      },
    }),
    enabled: !!schedulingOptions.course_id,
  });
  const sectionRows = (sections as { data: GetSectionsItem[]; total: number } | undefined)?.data;

  const { data: instructors } = useQuery({
    ...getCourseInstructorPoolOptions({
      path: {
        id: schedulingOptions.course_id ?? '',
      },
      query: {
        page: 1,
        pageSize: 100,
      },
    }),
    enabled: !!schedulingOptions.course_id,
  });

  const instructorData = instructors as { data: Instructor[]; total: number } | undefined;

  useEffect(() => {
    if (courses.length > 0 && schedulingOptions.course_id === undefined) {
      setSchedulingOptions((prevState) => ({
        ...prevState,
        course_id: courses?.[0]?.id,
        duration: moment.duration(courses?.[0]?.typical_period_duration ?? { minutes: 60 }).asMinutes(),
        capacity: courses?.[0]?.typical_period_capacity ?? 1,
        observationCapacity: courses?.[0]?.typical_period_observation_capacity ?? 0,
        waitlistCapacity: courses?.[0]?.typical_period_waitlist_capacity ?? 0,
      }));
      setSelectedCourseType(courses?.[0]?.course_type);
    }
    // console.log(schedulingOptions);
  }, [courses]);

  useEffect(() => {
    // set the section_id to the first section of the course if not already set
    if (sectionRows && schedulingOptions.section_id === undefined) {
      setSchedulingOptions((prevState) => ({ ...prevState, section_id: sectionRows?.[0]?.id ?? '' }));
    }
    // console.log(schedulingOptions);
  }, [sectionRows]);

  const handleClose = () => {
    initialScrollPosition.current = document.documentElement.scrollTop;
    setOpen(false); // close the dialog
    setTimeout(() => {
      // prevent chrome bug that scrolls page to top
      if (initialScrollPosition.current !== null) {
        window.scrollTo(0, initialScrollPosition.current);
        initialScrollPosition.current = null;
      }
    }, 0);
  };

  const openInstructorPanel = () => {
    setOpen(true);
  };

  const checkResourcesWithEvents = (uncheckOthers = false) => {
    setVisibleResources((prevState) => {
      if (uncheckOthers) {
        prevState.clear();
      }
      scheduler.current.getVisibleResources().forEach((instructor) => {
        prevState.add(instructor);
      });
      scheduler.current.setVisibleResources(prevState);
      return new Set(prevState);
    });
  };

  const uncheckAllResources = () => {
    const emptySelectionSet = new Set<string>();
    setVisibleResources(emptySelectionSet);
    scheduler.current.setVisibleResources(emptySelectionSet);
  };

  const updateSchedulingInterval = (interval: Interval) => {
    scheduler.current.setCurrentInterval(interval);
    setCurrentInterval(interval);
  };

  const reRender = () => {
    setShouldRerender((prevState) => !prevState);
  };

  const createDrives = (slotInfo: DateSelectArg) => {
    if (selectedCourseType) {
      scheduler.current.createDrives(slotInfo, schedulingOptions, selectedCourseType);
    } else {
      throw new Error('No course type selected for scheduling drives.');
    }
  };

  // const updateOldDriveQueryData = (queryKey: QueryKey, newDrives: Drive[]) => {
  //   const currentQueryData = queryClient.getQueryData(queryKey) as InfiniteData<SearchableDriveConnection>;
  //   console.log(currentQueryData);
  //   const lastPage = currentQueryData.pages.length - 1;

  //   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  //   newDrives.forEach((drive: Drive) => {
  //     currentQueryData.pages[lastPage].items.push(drive);
  //   });
  // };

  // const removeDriveFromQueryData = (queryKey: QueryKey, driveID: string) => {
  //   const currentQueryData = queryClient.getQueryData(queryKey) as InfiniteData<SearchableDriveConnection>;
  //   console.log(currentQueryData);
  //   currentQueryData.pages.forEach((page, index) => {
  //     const before = currentQueryData.pages[index].items.length;
  //     currentQueryData.pages[index].items = page.items.filter((item) => item && item.id !== driveID);
  //     console.log(`Page ${index}: ${before}, ${currentQueryData.pages[index].items.length}`);
  //   });
  // };

  const getCheckedResources = useCallback(() => {
    if (calendarData.instructors) {
      return calendarData.instructors.filter((resource: Instructor) => visibleResources.has(resource.user_id));
    }
    return [];
  }, [currentInterval, visibleResources]);

  const deleteDrive = async (coursePeriodIds: string[]) => {
    const result = await calendarData.deleteCoursePeriodsMutation.mutateAsync({ body: coursePeriodIds });
    coursePeriodIds.forEach((coursePeriodId) => {
      scheduler.current.driveTree.removeEvent(coursePeriodId);
      (calendarRef as any).current.getApi().getEventById(coursePeriodId).remove(); // remove it from the calendar
    });
    
    // Invalidate queries to refresh the calendar data
    queryClient.invalidateQueries({
      queryKey: listCoursePeriodsQueryKey({} as Options<ListCoursePeriodsData>),
      exact: false,
    });
    
    return result;
  };

  const panel = useMemo(
    () => (
      // console.log('re-rendered instructor panel');
      <>
        <SchedulingPanel
          open={open}
          options={schedulingOptions}
          setOptions={setSchedulingOptions}
          setSelectedCourseType={setSelectedCourseType}
          disabled={disableSelection}
          instructors={
            instructorData?.data?.sort((first, second) =>
              (first.user.first_name?.toLowerCase() ?? '') > (second.user.first_name?.toLowerCase() ?? '') ? 1 : -1,
            ) ?? []
          }
          visibleResources={visibleResources}
          toggleResource={(resourceID: string) => {
            console.log(`Toggle: ${resourceID}`);
            setVisibleResources(new Set(scheduler.current.toggleResource(resourceID)));
          }}
          eventCount={(resourceID: string) => {
            const events = scheduler.current.getVisibleResourceEvents(resourceID);
            // console.log(`Event count for ${instructors.find((instructor) => instructor.username === resourceID)!.firstName}`);
            // console.log({
            //   existingEvents: events.existingEvents.length,
            //   newEvents: events.newEvents.length,
            // });
            return {
              existingEvents: events.existingEvents.length,
              newEvents: events.newEvents.length,
            };
          }}
          disableSubmit={scheduler.current.newDriveStack.length === 0}
          onSubmit={async () => {
            try {
              // show confirmation dialog
              const confirmed = await dialogs.confirm(
                "You won't be able to revert this without deleting each individual period.",
                {
                  okText: 'Create',
                  cancelText: 'Cancel',
                },
              );

              // if user cancels, return
              if (!confirmed) {
                return;
              }

              const result = await scheduler.current.submitNewDrives((drivesToSubmit: unknown) =>
                calendarData.submitNewDrives.mutateAsync({ body: drivesToSubmit as never[] }),
              );
              console.log('Outside Result is: ', result);
              
              // Invalidate queries to refresh the calendar data
              queryClient.invalidateQueries({
                queryKey: listCoursePeriodsQueryKey({} as Options<ListCoursePeriodsData>),
                exact: false,
              });

              // show success message
              dialogs.open(FeedbackDialog, {
                title: 'Success',
                icon: 'success',
              });
            } catch (error) {
              dialogs.open(FeedbackDialog, {
                title: 'Error ',
                message: getApiErrorMessage(error),
              });
            }
          }}
          showUndo={scheduler.current.newDriveStack.length > 0}
          onUndo={() => {
            console.log('Undo');
            scheduler.current.undo();
            reRender(); // rerender to update drive count
          }}
        />
      </>
    ),
    [
      currentInterval,
      disableSelection,
      schedulingOptions,
      instructors,
      open,
      shouldRerender,
      visibleResources,
      calendarData.coursePeriods,
      courses,
      sections,
    ],
  );

  // return {
  //   openInstructorPanel,
  //   closeInstructorPanel: handleClose,
  //   SchedulingPanel: Panel,
  //   scheduler: scheduler.current,
  //   getCheckedResources,
  //   reRenderInstructorPanel: reRender,
  //   checkResourcesWithEvents,
  //   uncheckAllResources,
  //   updateSchedulingInterval,
  // };

  return {
    open: openInstructorPanel,
    close: handleClose,
    calendarData,
    deleteDrive,
    createDrives,
    disableSelection: setDisableSelection,
    getCheckedResources,
    reRender,
    checkResourcesWithEvents,
    uncheckAllResources,
    updateSchedulingInterval,
    options: schedulingOptions,
    selectedCourseType,
    scheduler: scheduler.current,
    panel,
  };
}
